import { __ } from '@99designs/i18n';
import type { Agreement } from './SOWModal';

export type SOWVariant = 'standard' | 'logoTouchUp';

export const formatDay = (days: number): { dayTotal: number; dayCopy: string } => ({
    dayTotal: days,
    dayCopy:
        days === 1
            ? __('day', {
                  _context:
                      'Singular form of day displayed to freelance designers in the design service statement of work near a numerical value of 1, e.g. 1 day or 1 business day',
              })
            : __('days', {
                  _context:
                      'Pluralised form of day displayed to freelance designers in the design service statement of work near a numerical value greater than 1, e.g. 7 days or 7 business days',
              }),
});

export interface SOWContent {
    // Intro
    introLine: string | null;
    firstParagraph: string;
    projectDescription: string;

    // Design Services
    designServicesIntro: string;
    inclusionBullets: string[];

    // Deliverables
    deliverableBullets: string[];
    artboardSpecs: string[] | null;
    qualityRequirement: string | null;

    // Timeline
    turnaroundText: string;
    closeText: string;

    // Earnings & Payment
    earningsText: string;
    approvalText: string;
    deliveryText: string;
    activityText: string;

    // Agreement
    agreementText: string | null;
}

export function getSOWContent(
    variant: SOWVariant,
    agreement: Agreement,
    params: { requestId: string; formattedEarnings: string; matchedDate: string }
): SOWContent {
    if (variant === 'logoTouchUp') {
        return getLogoTouchUpContent(agreement, params);
    }
    return getStandardContent(agreement, params);
}

function getLogoTouchUpContent(
    agreement: Agreement,
    {
        requestId,
        formattedEarnings,
        matchedDate: _matchedDate,
    }: { requestId: string; formattedEarnings: string; matchedDate: string }
): SOWContent {
    const turnaroundDay = formatDay(agreement.turnaround);
    const payDay = formatDay(agreement.payDeadline);
    const deadlineDay = formatDay(agreement.requestDeadline);

    return {
        introLine: __(
            'Before expressing interest in this logo touch-up and completion project, please review and agree to the following Statement of Work (SOW).'
        ),
        firstParagraph: __(
            'This Statement of Work (SOW) dated as of {{matchedDate}} ("SOW Effective Date") is entered into by and between Vista and You and is subject to the terms and conditions of the Master Design Service Agreement in all respects.',
            {
                matchedDate: _matchedDate || __('the project start date'),
            }
        ),
        projectDescription: __(
            'This SOW applies to the logo touch-up and completion project that is based upon the following Design Request from VistaPrint customer: {{requestId}}.',
            { requestId }
        ),

        designServicesIntro: __(
            'You agree that if you are selected to be the designer on this logo touch-up and completion project, you shall provide Vista with the following inclusions:'
        ),
        inclusionBullets: [
            __(
                'Touch-up and finalization of the AI-generated logo concept selected by the VistaPrint customer through the free AI logomaker',
                {
                    _context:
                        'Displayed to freelance designers in the logo touch-up statement of work describing the touch-up service they must provide',
                }
            ),
            __(
                'Up to {{revisions}} rounds of revisions to perfect the design based on feedback provided by the VistaPrint customer via the VistaPrint Site',
                {
                    revisions: agreement.revisions,
                    _context:
                        'Displayed to freelance designers in the logo touch-up statement of work that tells them the revision limit',
                }
            ),
        ],
        deliverableBullets: [
            __(
                "You must upload and deliver the initial touched-up logo design as well as any revised logo designs following the VistaPrint customer's submission of feedback through the Design Studio via the VistaPrint Site that is provided to you to work on the logo touch-up and completion project.",
                {
                    _context:
                        'Displayed to freelance designers in the logo touch-up statement of work instructing them on how to deliver their work',
                }
            ),
            __(
                'You must provide the initial touched-up logo design as well as any revised logo designs to Vista in high-resolution file formats suitable for digital use and future printing applications. All logo files must be delivered in vector format (AI, EPS, or SVG) at a minimum resolution of 300dpi when applicable, and provided in both RGB (for digital/screen use) and CMYK (for print use) color modes. Acceptable file formats include AI, PDF, SVG, EPS vector file formats. All logo files must meet the following artboard specifications:'
            ),
        ],
        artboardSpecs: [
            __('Size: 4000 x 4000 pixels'),
            __('Resolution: 300 dpi'),
            __('Orientation: Square'),
            __(
                "Spacing: The logo must be centered on the artboard and scaled as large as possible while maintaining a 150px 'safe zone' (padding) from the edges. This ensures the logo does not touch the artboard boundaries."
            ),
        ],
        qualityRequirement: __(
            "You must ensure all files are production-ready, scalable without loss of quality, meet Vista's quality standards and specifications, and are suitable for the VistaPrint customer's intended use.",
            {
                _context:
                    'Displayed to freelance designers in the logo touch-up statement of work specifying general quality requirements',
            }
        ),
        turnaroundText: __(
            `You must deliver your initial touched-up logo design within {{dayTotal}} business {{dayCopy}} following the SOW Effective Date and deliver revised logo designs within {{dayTotal}} business {{dayCopy}} following the VistaPrint customer's submission of feedback via the VistaPrint Site on the initial touched-up logo design or revision, as the case may be. For purposes of this provision, "business days" excludes weekends.`,
            {
                ...turnaroundDay,
                _context:
                    'Displayed to freelance designers in the logo touch-up statement of work that tells them the number of days they have to complete the work for the customer',
            }
        ),
        closeText: __(
            `The logo touch-up and completion project will automatically close {{dayTotal}} {{dayCopy}} after the SOW effective date and the VistaPrint customer will no longer be able to request revisions.`,
            {
                ...deadlineDay,
                _context:
                    'Displayed to freelance designers in the logo touch-up statement of work that tells them the maximum deadline for a project',
            }
        ),
        earningsText: __(
            `Your earnings for completing this logo touch-up and completion project will be {{earnings}} which will be paid as Credits into your account, upon full and satisfactory completion of the Design Request which will be determined at Vista's sole discretion.`,
            { earnings: formattedEarnings }
        ),
        approvalText: __(
            'The customer approves the initial touched-up logo design or any revised logo designs OR'
        ),
        deliveryText: __(
            '{{dayTotal}} {{dayCopy}} after you deliver the initial touched-up logo design or any revised logo designs and there is no activity from the VistaPrint customer',
            {
                ...payDay,
                _context:
                    'Displayed to freelance designers in the logo touch-up statement of work that informs them of the conditions for them to be paid',
            }
        ),
        activityText: __(
            '"Activity" refers to any action by the VistaPrint customer related to this logo touch-up and completion project, including a request for revision. If the VistaPrint customer requests a revision, the {{dayTotal}} day payment timeframe is cancelled and will restart when you deliver the revised logo design in accordance with the revision request.',
            {
                dayTotal: agreement.payDeadline,
                _context:
                    'Displayed to freelance designers in the logo touch-up statement of work that informs them of the conditions for them to be paid',
            }
        ),
        agreementText: __(
            'I agree that if I am selected by the VistaPrint customer as the designer for this logo touch-up and completion project, I will fulfill the Design Services and Deliverables in accordance with the terms outlined in this Statement of Work and the MDSA.'
        ),
    };
}

function getStandardContent(
    agreement: Agreement,
    {
        requestId,
        formattedEarnings,
        matchedDate,
    }: { requestId: string; formattedEarnings: string; matchedDate: string }
): SOWContent {
    const turnaroundDay = formatDay(agreement.turnaround);
    const payDay = formatDay(agreement.payDeadline);
    const deadlineDay = formatDay(agreement.requestDeadline);
    return {
        introLine: null,
        firstParagraph: __(
            'This Statement of Work (SOW) dated as of {{matchedDate}}, is entered into by and between Vista and You and is subject to the terms and conditions of the Master Design Service Agreement in all respects.',
            {
                matchedDate: matchedDate || __('the project start date'),
                _context: `This Statement of Work is shown to designers after they've been accepted for the project as a reminder of what they've agreed to.`,
            }
        ),
        projectDescription: __(
            'This SOW applies to the design project that is based upon the following Design Request from VistaPrint customer: {{requestId}}.',
            { requestId }
        ),

        designServicesIntro: __('You shall provide Vista with the following inclusions:'),

        inclusionBullets: [
            __(
                'At least one {{deliverables}} {{deliverableType}} based on and responsive to the requirements and specifications of the Design Request.',
                {
                    deliverables: agreement.deliverables,
                    deliverableType: agreement.deliverableType,
                    _context:
                        'Displayed to freelance designers in the design service statement of work, that instructs them on the type and quantity of the deliverables they must produce for a customer',
                }
            ),
            __(
                'Up to {{revisions}} rounds of revisions based on feedback provided by the VistaPrint customer via the VistaPrint Site following the provision of the initial design concept.',
                {
                    revisions: agreement.revisions,
                    _context:
                        'Displayed to freelance designers in the design service statement of work that tells them the revision limit',
                }
            ),
        ],

        deliverableBullets: [
            __(
                "You must upload and deliver the initial design as well as any revised designs following the VistaPrint customer's submission of the feedback on the initial design concept or revision through the Design Studio via the VistaPrint Site that is provided to you to work on the design project."
            ),
            __(
                "You must provide the initial design as well as any revised designs to Vista in high-resolution file format that are suitable for commercial printing (atleast 300dpi in CMYK color mode). Acceptable file formats include AI, PDF, SVG, EPS vector file formats. You must ensure all files are print-ready, meet Vista's print and quality standards and specifications, and are fully editable to allow for future adaptation or use by Vista."
            ),
        ],

        artboardSpecs: null,
        qualityRequirement: null,

        turnaroundText: __(
            `You must deliver your initial design concept within {{dayTotal}} business {{dayCopy}} following the SOW Effective Date and deliver revisions within {{dayTotal}} business {{dayCopy}} following the VistaPrint customer's submission of feedback via the VistaPrint Site on the initial design concept or revision, as the case may be. For purposes of this provision, "business days" excludes weekends.`,
            {
                ...turnaroundDay,
                _context:
                    'Displayed to freelance designers in the design service statement of work that tells them the number of days they have to complete the work for the customer',
            }
        ),

        closeText: __(
            'The design project will automatically close {{dayTotal}} {{dayCopy}} after the SOW effective date and the VistaPrint customer will no longer be able to request revisions.',
            {
                ...deadlineDay,
                _context:
                    'Displayed to freelance designers in the design service statement of work that tells them the maximum deadline for a project',
            }
        ),

        earningsText: __(
            "Your earnings for completing this design project will be {{earnings}} which will be paid as Credits into your account, upon full and satisfactory completion of the Design Request which will be determined at Vista's sole discretion.",
            { earnings: formattedEarnings }
        ),

        approvalText: __(
            'The customer approves the initial design concept or any revised designs OR'
        ),

        deliveryText: __(
            '{{dayTotal}} {{dayCopy}} after you deliver the initial design concept or any revised designs and there is no activity from the VistaPrint customer',
            {
                ...payDay,
                _context:
                    'Displayed to freelance designers in the design service statement of work that informs them of the conditions for them to be paid',
            }
        ),

        activityText: __(
            '"Activity" refers to any action by the VistaPrint customer related to this design project, including a request for revision. If the VistaPrint customer requests a revision, the {{dayTotal}} day payment timeframe is cancelled and will restart when you deliver the revised design in accordance with the revision request.',
            {
                dayTotal: agreement.payDeadline,
                _context:
                    'Displayed to freelance designers in the design service statement of work that informs them of the conditions for them to be paid',
            }
        ),
        agreementText: null,
    };
}
